import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Compass, BookOpen, Flame, Sparkles, TreePine, Feather, Target,
  KeyRound, Sunrise, Bell, ScrollText, User,
} from "lucide-react";
import {
  Button, JourneyCard, CollectionOval, PanelRow, SanskritSubtitle,
} from "../design-system";
import { getJourney, startJourney } from "../lib/previewJourney";
import "./HomePage.css";

/**
 * iCRAFTZ · Homepage · Sprint 1 · Increment 2
 * Real DOM rebuild of the frozen painted mockup. Composition, hierarchy,
 * spacing rhythm and vintage warmth are preserved. Every visible element
 * is a Design System component; no PNG overlays remain.
 */

const NAV = [
  { label: "Library",       route: "/library" },
  { label: "Collections",   route: "/collections" },
  { label: "MyCraftz",      route: "/mycraftz" },     // D23 corollary — was "My Crafts"
  { label: "My Sanctuary",  route: "/my-sanctuary" },
];

const JOURNEY_STAGES = [
  { key: "discover",  english: "Discover",  sanskrit: "Jigyasa",  subcopy: "Find ideas worth exploring",     icon: Compass },
  { key: "learn",     english: "Learn",     sanskrit: "Bodha",    subcopy: "Understand what truly matters",  icon: BookOpen },
  { key: "commit",    english: "Commit",    sanskrit: "Sankalpa", subcopy: "Choose one idea to live by",     icon: Flame },
  { key: "practice",  english: "Practice",  sanskrit: "Sadhana",  subcopy: "Apply daily with discipline",    icon: Sparkles },
  { key: "transform", english: "Transform", sanskrit: "Siddhi",   subcopy: "Become who you want to be",      icon: TreePine },
];

const COLLECTIONS = [
  { key: "habits",     title: "Habits",           icon: Compass },
  { key: "focus",      title: "Focus & Deep Work", icon: BookOpen },
  { key: "wealth",     title: "Wealth & Money",   icon: Sparkles },
  { key: "mindset",    title: "Mindset & Growth", icon: Target },
  { key: "leadership", title: "Leadership",       icon: Flame },
  { key: "purpose",    title: "Purpose & Life",   icon: TreePine },
];

const PANEL_ROWS = [
  { icon: Feather,    title: "AI-Curated Wisdom Artifacts", desc: "Extracted from the world's best books" },
  { icon: Target,     title: "Rule of Three",               desc: "Focus on what truly transforms" },
  { icon: KeyRound,   title: "CraftPrint™",                 desc: "Your 30-day personalized action plan" },
  { icon: Flame,      title: "Daily Sadhana",               desc: "Build habits. Build identity." },
  { icon: Sunrise,    title: "My Sanctuary",                desc: "Your space for reflections and insights", route: "/my-sanctuary" },
  { icon: TreePine,   title: "Siddhi",                      desc: "Measure transformation, not just progress" },
];

const FOOTER_TABS = [
  { key: "home",        label: "Home",         icon: Compass,    route: "/",            active: true },
  { key: "library",     label: "Library",      icon: BookOpen,   route: "/library" },
  { key: "collections", label: "Collections",  icon: Sparkles,   route: "/collections" },
  { key: "mycraftz",    label: "MyCraftz",     icon: ScrollText, route: "/mycraftz" },
  { key: "sanctuary",   label: "Sanctuary",    icon: Sunrise,    route: "/my-sanctuary" },
  { key: "profile",     label: "Profile",      icon: User,       route: "/login" },
];

export default function HomePage() {
  const navigate = useNavigate();
  useEffect(() => { document.title = "iCRAFTZ"; }, []);

  const go = (route) => () => navigate(route);

  const routeStage = (stageKey) => {
    const j = getJourney();
    const book = j?.book || "";
    const stage = j?.stage || null;
    const order = ["bodha", "sankalpa", "craftprint", "sadhana", "workspace"];
    const past = (s) => order.indexOf(stage) >= order.indexOf(s);

    if (stageKey === "discover")   return navigate("/library");
    if (stageKey === "learn")      return book ? navigate(`/bodha?mode=preview&book=${book}`) : navigate("/library");
    if (stageKey === "commit")     return book && past("sankalpa") ? navigate(`/sankalpa?mode=preview&book=${book}`) : navigate("/library");
    if (stageKey === "practice")   return book && past("sadhana") ? navigate(`/sadhana?mode=preview&book=${book}`) : navigate("/library");
    if (stageKey === "transform")  return navigate("/mycraftz?mode=preview");   // D30 · Round 3
    return navigate("/library");
  };

  const beginJourney = () => {
    // Fresh journey — user picks their book on Library.
    navigate("/library");
  };

  return (
    <main className="home" data-testid="homepage">
      {/* Ornamental parchment frame — the vintage warmth */}
      <div className="home__frame" aria-hidden="true">
        <span className="home__frame-corner home__frame-corner--tl" />
        <span className="home__frame-corner home__frame-corner--tr" />
        <span className="home__frame-corner home__frame-corner--bl" />
        <span className="home__frame-corner home__frame-corner--br" />
      </div>

      <div className="home__inner">
        {/* ── Top navigation ── */}
        <nav className="home__nav" data-testid="home-nav" aria-label="Primary">
          <a className="home__brand" href="/" data-testid="home-brand" aria-label="iCRAFTZ — Home">
            <img src="/brand/icraftz-emblem.png" alt="" className="home__brand-mark" />
            <span className="home__brand-word">iCRAFTZ</span>
          </a>
          <ul className="home__nav-links">
            {NAV.map((n) => (
              <li key={n.label}>
                <button
                  className="home__nav-link"
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={go(n.route)}
                >
                  {n.label}
                </button>
              </li>
            ))}
          </ul>
          <button className="home__bell" data-testid="home-bell" aria-label="Notifications">
            <Bell size={18} />
          </button>
        </nav>

        {/* ── Hero ── */}
        <section className="home__hero" data-testid="home-hero">
          <p className="icz-eyebrow">A Behavioural Transformation Engine</p>
          <h1 className="home__title icz-display-xl">iCRAFTZ</h1>
          <p className="icz-tagline home__hero-tag">Where you craft who you become</p>
          <p className="home__hero-body icz-body">
            Timeless wisdom from the world's greatest books, translated into daily practice.
            One idea at a time. One habit at a time.
          </p>
          <div className="home__hero-ctas">
            <Button variant="primary" size="lg" onClick={beginJourney} data-testid="cta-begin">
              Begin Your Journey <Sparkles size={14} />
            </Button>
            <Button variant="ghost" size="lg" onClick={go("/library")} data-testid="cta-explore">
              Explore Library
            </Button>
          </div>
        </section>

        {/* ── Two-column body: journey/collections + info panel ── */}
        <section className="home__body" data-testid="home-body">
          <div className="home__body-main">
            {/* How It Works — 5 tiles */}
            <div className="home__section-head">
              <p className="icz-eyebrow">How It Works</p>
              <h2 className="icz-h1 home__section-title">The Path to Transformation</h2>
            </div>
            <div className="home__journey" data-testid="home-journey">
              {JOURNEY_STAGES.map((s, i) => {
                const Icon = s.icon;
                return (
                  <JourneyCard
                    key={s.key}
                    data-testid={`how-${s.key}`}
                    icon={<Icon size={30} />}
                    english={s.english}
                    sanskrit={s.sanskrit}
                    subcopy={s.subcopy}
                    onClick={() => routeStage(s.key)}
                    style={{ "--i": i }}
                    className="icz-stagger"
                  />
                );
              })}
            </div>

            {/* Popular Collections */}
            <div className="home__section-head home__section-head--split">
              <div>
                <p className="icz-eyebrow">Explore</p>
                <h2 className="icz-h1 home__section-title">Popular Collections</h2>
              </div>
              <Button variant="link" onClick={go("/collections")} data-testid="collections-view-all">
                View all →
              </Button>
            </div>
            <div className="home__collections" data-testid="home-collections">
              {COLLECTIONS.map((c) => {
                const Icon = c.icon;
                return (
                  <CollectionOval
                    key={c.key}
                    data-testid={`collection-${c.key}`}
                    icon={<Icon size={40} />}
                    title={c.title}
                    onClick={go("/collections")}
                  />
                );
              })}
            </div>
          </div>

          {/* Right info panel */}
          <aside className="home__panel" data-testid="home-panel" aria-label="What makes iCRAFTZ different">
            <p className="icz-eyebrow">What makes iCRAFTZ different</p>
            <div className="home__panel-rows">
              {PANEL_ROWS.map((r) => {
                const Icon = r.icon;
                return (
                  <PanelRow
                    key={r.title}
                    data-testid={`panel-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    icon={<Icon size={18} />}
                    title={r.title}
                    description={r.desc}
                    onClick={r.route ? go(r.route) : undefined}
                  />
                );
              })}
            </div>
          </aside>
        </section>

        {/* ── Footer tab bar ── */}
        <nav className="home__tabs" data-testid="home-tabs" aria-label="Mobile navigation">
          {FOOTER_TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                className={`home__tab ${t.active ? "home__tab--active" : ""}`}
                data-testid={`tab-${t.key}`}
                onClick={go(t.route)}
                aria-current={t.active ? "page" : undefined}
              >
                <Icon size={18} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </main>
  );
}
